import { message } from "antd";
import { http } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import {
  agentPrincipalHint,
  validAgentOrigin,
  validAgentIdentityRequest,
  type AgentIdentityRequest,
} from "./agentIdentity";

interface EmbedApi {
  version: 1;
  mount(id: string, origin: string): void;
  destroy(id: string): void;
  setIdentityProvider(
    id: string,
    provider: ((request: AgentIdentityRequest) => Promise<string | null>) | null,
  ): void;
}

/** Production opt-in. Default local builds retain their existing development entry. */
export function installAuthenticatedGuideAgent(
  origin: string | undefined,
  integration = "trust-guide",
  allowHttpLoopback = false,
): () => void {
  if (!origin) return () => {};
  const parentOrigin = window.location.origin;
  try {
    if (
      !validAgentOrigin(origin, allowHttpLoopback) ||
      !validAgentOrigin(parentOrigin, allowHttpLoopback) ||
      !/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(integration)
    )
      return () => {};
  } catch {
    return () => {};
  }
  const api = () => (window as Window & { AgentServerEmbed?: EmbedApi }).AgentServerEmbed;
  const principal = () => {
    const state = useAuthStore.getState();
    return agentPrincipalHint(state.accessToken, state.loggedIn);
  };
  let generation = 0;
  let selected: string | null | undefined;
  let stopped = false;
  let loader: Promise<void> | undefined;
  const ensureLoader = () => {
    if (loader) return loader;
    if (api()) return Promise.resolve();
    loader ??= new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      const timer = setTimeout(() => {
        script.remove();
        reject(new Error("unavailable"));
      }, 10000);
      script.dataset.agentserverManual = "true";
      script.src = `${origin}/embed/v1/loader.js?integration=${encodeURIComponent(integration)}`;
      script.onload = () => {
        clearTimeout(timer);
        resolve();
      };
      script.onerror = () => {
        clearTimeout(timer);
        script.remove();
        reject(new Error("unavailable"));
      };
      document.body.append(script);
    });
    return loader;
  };
  const update = () => {
    const key = principal();
    if (key === selected || stopped) return;
    selected = key;
    const ownGeneration = ++generation;
    api()?.destroy(integration);
    api()?.setIdentityProvider?.(integration, null);
    if (!key) return;
    void ensureLoader()
      .then(() => {
        const embed = api();
        if (stopped || ownGeneration !== generation || principal() !== key) {
          embed?.destroy(integration);
          return;
        }
        if (!embed?.setIdentityProvider) {
          embed?.destroy(integration);
          throw new Error("unavailable");
        }
        embed.setIdentityProvider(integration, async (request) => {
          if (
            stopped ||
            ownGeneration !== generation ||
            principal() !== key ||
            !validAgentIdentityRequest(request, integration, parentOrigin, origin)
          )
            return null;
          const { credentialPath: _path, ...body } = request;
          const response = await http<{ assertion: string }>("/agent/identity", {
            method: "POST",
            body,
            signal: AbortSignal.timeout(8000),
            redirect: "error",
          });
          if (stopped || ownGeneration !== generation || principal() !== key) return null;
          return typeof response.assertion === "string" && response.assertion.length <= 8192
            ? response.assertion
            : null;
        });
        embed.mount(integration, origin);
      })
      .catch(() => {
        if (!stopped && ownGeneration === generation)
          void message.error("助手暂不可用，请刷新页面后重试。");
      });
  };
  const unsubscribe = useAuthStore.subscribe(update);
  update();
  return () => {
    stopped = true;
    generation++;
    unsubscribe();
    api()?.destroy(integration);
    api()?.setIdentityProvider?.(integration, null);
  };
}
